<script setup lang="ts">
import type { SplitterItem } from '@nuxt/ui'
import type { GameVehicleSummary, GameVehicleDetail, WikiVehicle } from "~~/types/gamedata";

defineProps({
  vehicle: {
    type: Object as () => GameVehicleSummary,
    required: true,
  },
  detail: {
    type: Object as () => GameVehicleDetail,
    required: true,
  },
  raw: {
    type: Object as () => WikiVehicle | null,
    required: true,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  style: {
    type: String as () => 'default' | 'ams',
    default: () => 'default',
  },
});

const splitterItems: SplitterItem[] = [
  { slot: 'main', minSize: 20, defaultSize: 67 },
  { slot: 'right', minSize: 20, defaultSize: 33 },
]
</script>

<template>
  <div>
    <UAlert
      v-if="vehicle.isConcept"
      class="mb-4"
      color="warning"
      variant="subtle"
      icon="i-lucide-info"
      title="Konzeptschiff"
      description="Dieses Schiff befindet sich in der Konzeptphase — technische Daten sind eventuell unvollständig oder werden sich noch stark ändern."
    />
    <USplitter
      id="shipexkurs-hero-splitter"
      auto-save-id="shipexkurs-hero-splitter"
      :items="splitterItems"
      :ui="{ handle: 'w-px bg-border transition-colors data-[state=hover]:bg-primary data-[state=drag]:bg-primary relative group' }"
      class="gap-4"
    >
      <template #resize-handle>
        <ShipexkursShipHeroResizeHandle />
      </template>
      <template #main>
        <div class="space-y-4">
          <ShipexkursShipHeroMainHeader :vehicle="vehicle" />
          <ShipexkursShipHeroMainImage :vehicle="vehicle" :images="detail.images" />
          <div
            v-if="loading"
            class="ml-1 flex items-center gap-2 border-l-2 border-l-primary pl-4 text-sm text-neutral-500"
          >
            <UIcon name="i-lucide-loader-2" class="size-4 animate-spin" />
            Beschreibung und technische Details werden geladen…
          </div>
          <div
            v-else-if="detail.description"
            class="ml-1 border-l-2 border-l-primary pl-4 text-sm text-justify"
          >
            {{ detail.description }}
          </div>
          <ShipexkursShipHeroMainTabs :raw="raw" :ship-id="vehicle.id" />
        </div>
      </template>
      <template #right>
        <div class="block size-full space-y-4">
          <ShipexkursShipHeroManufacturerBanner :manufacturer="vehicle.manufacturer" />
          <ShipexkursShipHeroSidebarBase :vehicle="vehicle" />
          <template v-if="raw">
            <ShipexkursShipHeroSidebarDimensions v-if="!SHIP_IDS_WITHOUT_DIMENSIONS_CARD.has(vehicle.id)" :vehicle="vehicle" :raw="raw" />
            <ShipexkursShipHeroSidebarCapacity :vehicle="vehicle" :raw="raw" />
            <ShipexkursShipHeroSidebarPrice :vehicle="vehicle" :raw="raw" />
            <ShipexkursShipHeroSidebarLinks :raw="raw" />
            <ShipexkursShipHeroSidebarMeta :raw="raw" />
          </template>
          <UCard v-else-if="loading" variant="outline" class="border border-default bg-bunker-950 ring-0">
            <div class="flex items-center gap-2 text-sm text-neutral-500">
              <UIcon name="i-lucide-loader-2" class="size-4 animate-spin" />
              Weitere Kennzahlen werden geladen…
            </div>
          </UCard>
        </div>
      </template>
    </USplitter>
  </div>
</template>
